import React, { useState } from 'react';

interface SaveForLaterModalProps {
  onClose: () => void;
  onSaveLocalOnly: () => void;
  // Returns the raw /draft response shape needed by the caller to decide what to show next.
  onEmailSubmit: (email: string) => Promise<{
    promoted: boolean;
    resume_path: string | null;
  }>;
}

// Public-route modal (outside <Layout>) offered when an anonymous/open-token filler clicks
// "Save Progress" — see specs/completed/form-builder-styling-pagination-resume.md §5. Two
// explicit exits, neither a dead end: emailing a resume link (server-side identity + cross-device
// resume) or staying purely local (same-device only, already-built localStorage safety net).
const SaveForLaterModal: React.FC<SaveForLaterModalProps> = ({ onClose, onSaveLocalOnly, onEmailSubmit }) => {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  // null = form not yet submitted; otherwise the result of the last submit attempt.
  const [result, setResult] = useState<{ promoted: boolean; resumePath: string | null } | null>(null);
  const [copied, setCopied] = useState(false);

  const handleSend = async () => {
    const trimmed = email.trim();
    if (!trimmed || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setSending(true);
    try {
      const res = await onEmailSubmit(trimmed);
      setResult({ promoted: res.promoted, resumePath: res.resume_path });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : "Couldn't send your link — please try again.");
    } finally {
      setSending(false);
    }
  };

  const resumeUrl = result?.resumePath
    ? `${window.location.origin}${result.resumePath}`
    : null;

  const handleCopy = async () => {
    if (!resumeUrl) return;
    try {
      await navigator.clipboard.writeText(resumeUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can be unavailable (older browsers, non-HTTPS) — the link is still
      // selectable text in the input, so this is a nice-to-have, not required.
    }
  };

  return (
    <div className="pff-modal-overlay" onClick={onClose}>
      <div className="pff-modal" onClick={(e) => e.stopPropagation()}>
        {!result ? (
          <>
            <h2 className="pff-modal-title">Want to finish this later?</h2>
            <p className="pff-modal-desc">We'll email you a private link so you can pick up right where you left off — on any device.</p>
            <input
              type="email"
              className="pff-input"
              placeholder="you@example.com"
              value={email}
              disabled={sending}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              autoFocus
            />
            {error && <p className="pff-error">{error}</p>}
            <div className="pff-modal-actions">
              <button type="button" className="pff-nav-btn" onClick={onSaveLocalOnly} disabled={sending}>
                Just save on this device
              </button>
              <button type="button" className="pff-nav-btn pff-nav-btn-primary" onClick={handleSend} disabled={sending}>
                {sending ? 'Sending…' : 'Email me my link'}
              </button>
            </div>
          </>
        ) : result.promoted && resumeUrl ? (
          <>
            <h2 className="pff-modal-title">Check your inbox</h2>
            <p className="pff-modal-desc">
              We've emailed your link — and here it is too, just in case:
            </p>
            <div className="pff-modal-link-row">
              <input type="text" className="pff-input" readOnly value={resumeUrl} onFocus={(e) => e.target.select()} />
              <button type="button" className="pff-nav-btn" onClick={handleCopy}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="pff-modal-actions">
              <button type="button" className="pff-nav-btn pff-nav-btn-primary" onClick={onClose}>
                Continue filling out the form
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="pff-modal-title">Saved on this device</h2>
            <p className="pff-modal-desc">
              We couldn't email you a link right now, but your progress is safely saved on this
              device — come back to this same link any time.
            </p>
            <div className="pff-modal-actions">
              <button type="button" className="pff-nav-btn pff-nav-btn-primary" onClick={onClose}>
                Continue filling out the form
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SaveForLaterModal;
